// components/ZoneModal.jsx
import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  GoogleMap,
  useJsApiLoader,
  Marker,
  StandaloneSearchBox,
} from "@react-google-maps/api";
import "./css/Zones.css";

const ZONE_TYPES = ["NATIONAL", "REGIONAL", "AREA", "TERRITORY"];
const LIBRARIES = ["places"];

const MAP_STYLE = [
  {
    featureType: "poi",
    elementType: "labels",
    stylers: [{ visibility: "off" }],
  },
  { featureType: "transit", stylers: [{ visibility: "off" }] },
];

const MAP_CONTAINER = { width: "100%", height: "300px", borderRadius: "10px" };

const INDIA_CENTER = { lat: 20.5937, lng: 78.9629 };

const EMPTY = {
  name: "",
  type: "REGIONAL",
  parentZoneId: "",
  description: "",
  location: {
    address: "",
    city: "",
    state: "",
    pincode: "",
    coordinates: { lat: "", lng: "" },
  },
};

// ── Reverse geocode helper ──────────────────────────────────────────────────
const reverseGeocode = (lat, lng, callback) => {
  if (!window.google) return;
  const geocoder = new window.google.maps.Geocoder();
  geocoder.geocode({ location: { lat, lng } }, (results, status) => {
    if (status !== "OK" || !results[0]) return;
    const comps = results[0].address_components;
    const get = (type) =>
      comps.find((c) => c.types.includes(type))?.long_name || "";

    callback({
      address: results[0].formatted_address.split(",")[0],
      city: get("locality") || get("administrative_area_level_3"),
      state: get("administrative_area_level_1"),
      pincode: get("postal_code"),
    });
  });
};

// ── Main Component ─────────────────────────────────────────────────────────
const ZoneModal = ({ isOpen, onClose, onSave, zone, siblingZones = [] }) => {
  const [form, setForm] = useState(EMPTY);
  const [mapOpen, setMapOpen] = useState(false);
  const [markerPos, setMarkerPos] = useState(null);
  const [mapCenter, setMapCenter] = useState(INDIA_CENTER);
  const searchBoxRef = useRef(null);
  const mapRef = useRef(null);

  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
    libraries: LIBRARIES,
  });

  // ── Reset when modal opens ────────────────────────────────────────────────
  useEffect(() => {
    if (!isOpen) return;

    const initCoords =
      zone?.location?.coordinates?.lat && zone?.location?.coordinates?.lng
        ? {
            lat: parseFloat(zone.location.coordinates.lat),
            lng: parseFloat(zone.location.coordinates.lng),
          }
        : null;

    setForm(
      zone
        ? {
            name: zone.name || "",
            type: zone.type || "REGIONAL",
            parentZoneId: zone.parentZoneId || "",
            description: zone.description || "",
            location: {
              address: zone.location?.address || "",
              city: zone.location?.city || "",
              state: zone.location?.state || "",
              pincode: zone.location?.pincode || "",
              coordinates: {
                lat: zone.location?.coordinates?.lat || "",
                lng: zone.location?.coordinates?.lng || "",
              },
            },
          }
        : EMPTY,
    );

    setMarkerPos(initCoords);
    setMapCenter(initCoords || INDIA_CENTER);
    setMapOpen(false);
  }, [zone, isOpen]);

  // ── Helpers ───────────────────────────────────────────────────────────────
  const set = (key, val) => setForm((p) => ({ ...p, [key]: val }));
  const setLoc = (key, val) =>
    setForm((p) => ({ ...p, location: { ...p.location, [key]: val } }));
  const setCoord = (key, val) =>
    setForm((p) => ({
      ...p,
      location: {
        ...p.location,
        coordinates: { ...p.location.coordinates, [key]: val },
      },
    }));

  // ── Apply coords + reverse geocode ───────────────────────────────────────
  const applyCoords = useCallback((lat, lng) => {
    const latStr = lat.toFixed(6);
    const lngStr = lng.toFixed(6);

    setMarkerPos({ lat, lng });
    setForm((p) => ({
      ...p,
      location: {
        ...p.location,
        coordinates: { lat: latStr, lng: lngStr },
      },
    }));

    reverseGeocode(lat, lng, ({ address, city, state, pincode }) => {
      setForm((p) => ({
        ...p,
        location: {
          address: address || p.location.address,
          city: city || p.location.city,
          state: state || p.location.state,
          pincode: pincode || p.location.pincode,
          coordinates: { lat: latStr, lng: lngStr },
        },
      }));
    });
  }, []);

  // ── Map click ─────────────────────────────────────────────────────────────
  const handleMapClick = useCallback(
    (e) => applyCoords(e.latLng.lat(), e.latLng.lng()),
    [applyCoords],
  );

  // ── Marker drag end ───────────────────────────────────────────────────────
  const handleMarkerDragEnd = useCallback(
    (e) => applyCoords(e.latLng.lat(), e.latLng.lng()),
    [applyCoords],
  );

  // ── Places SearchBox ──────────────────────────────────────────────────────
  const onSearchBoxLoad = (ref) => {
    searchBoxRef.current = ref;
  };

  const onPlacesChanged = () => {
    const places = searchBoxRef.current?.getPlaces();
    if (!places || places.length === 0) return;

    const place = places[0];
    const location = place.geometry?.location;
    if (!location) return;

    const lat = location.lat();
    const lng = location.lng();

    setMapCenter({ lat, lng });
    mapRef.current?.panTo({ lat, lng });
    mapRef.current?.setZoom(14);
    applyCoords(lat, lng);
  };

  // ── Jump to manual lat/lng input ──────────────────────────────────────────
  const jumpToManualCoords = () => {
    const lat = parseFloat(form.location.coordinates.lat);
    const lng = parseFloat(form.location.coordinates.lng);
    if (isNaN(lat) || isNaN(lng)) return;
    setMarkerPos({ lat, lng });
    setMapCenter({ lat, lng });
    mapRef.current?.panTo({ lat, lng });
    mapRef.current?.setZoom(14);
  };

  const handleSubmit = () => {
    if (!form.name.trim()) return;
    onSave({ ...form, parentZoneId: form.parentZoneId || null });
    onClose();
  };

  const hasCoords =
    form.location.coordinates.lat && form.location.coordinates.lng;

  // Early return AFTER all hooks — avoids "rendered fewer hooks" error
  if (!isOpen) return null;

  return (
    <div className="zone-overlay" onClick={onClose}>
      <div
        className="zone-modal"
        onClick={(e) => e.stopPropagation()}
        style={{ maxWidth: 620, width: "100%" }}
      >
        {/* Header */}
        <div className="zone-modal-header">
          <h2>{zone ? "Edit Zone" : "New Zone"}</h2>
          <button className="close-btn" onClick={onClose}>
            ✕
          </button>
        </div>

        <div className="zone-modal-body">
          {/* ── Zone Info ────────────────────────────────────────────────── */}
          <section className="zone-section">
            <h4>Zone info</h4>

            <div className="form-row">
              <div className="form-group">
                <label>Zone name *</label>
                <input
                  value={form.name}
                  onChange={(e) => set("name", e.target.value)}
                  placeholder="e.g. Tamil Nadu Region"
                />
              </div>
              <div className="form-group">
                <label>Type *</label>
                <select
                  value={form.type}
                  onChange={(e) => set("type", e.target.value)}
                >
                  {ZONE_TYPES.map((t) => (
                    <option key={t} value={t}>
                      {t}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="form-group">
              <label>
                Parent Zone{" "}
                <span style={{ color: "#aaa", fontWeight: 400 }}>
                  (optional)
                </span>
              </label>
              <select
                value={form.parentZoneId}
                onChange={(e) => set("parentZoneId", e.target.value)}
              >
                <option value="">— Top Level —</option>
                {siblingZones
                  .filter((z) => !zone || z.zoneId !== zone.zoneId)
                  .map((z) => (
                    <option key={z.zoneId} value={z.zoneId}>
                      [{z.type}] {z.name}
                    </option>
                  ))}
              </select>
              <small style={{ color: "#888", fontSize: 11 }}>
                NATIONAL → REGIONAL → AREA → TERRITORY
              </small>
            </div>

            <div className="form-group">
              <label>Description</label>
              <input
                value={form.description}
                onChange={(e) => set("description", e.target.value)}
                placeholder="Optional note"
              />
            </div>
          </section>

          {/* ── Location ─────────────────────────────────────────────────── */}
          <section className="zone-section">
            <h4>
              Location{" "}
              <span style={{ color: "#aaa", fontWeight: 400, fontSize: 12 }}>
                (optional)
              </span>
            </h4>

            <div className="form-group">
              <label>Address</label>
              <input
                value={form.location.address}
                onChange={(e) => setLoc("address", e.target.value)}
                placeholder="Street / Area"
                autoComplete="new-password"
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>City</label>
                <input
                  value={form.location.city}
                  onChange={(e) => setLoc("city", e.target.value)}
                  placeholder="City"
                  autoComplete="off"
                />
              </div>
              <div className="form-group">
                <label>State</label>
                <input
                  value={form.location.state}
                  onChange={(e) => setLoc("state", e.target.value)}
                  placeholder="State"
                  autoComplete="off"
                />
              </div>
              <div className="form-group form-group--sm">
                <label>Pincode</label>
                <input
                  value={form.location.pincode}
                  onChange={(e) => setLoc("pincode", e.target.value)}
                  placeholder="641001"
                  autoComplete="off"
                />
              </div>
            </div>

            {/* Coordinates + Map toggle */}
            <div className="form-row" style={{ alignItems: "flex-end" }}>
              <div className="form-group">
                <label>Latitude</label>
                <input
                  type="number"
                  value={form.location.coordinates.lat}
                  onChange={(e) => setCoord("lat", e.target.value)}
                  onBlur={jumpToManualCoords}
                  placeholder="11.0168"
                />
              </div>
              <div className="form-group">
                <label>Longitude</label>
                <input
                  type="number"
                  value={form.location.coordinates.lng}
                  onChange={(e) => setCoord("lng", e.target.value)}
                  onBlur={jumpToManualCoords}
                  placeholder="76.9558"
                />
              </div>
              <div
                className="form-group form-group--sm"
                style={{ flexShrink: 0 }}
              >
                <label>&nbsp;</label>
                <button
                  type="button"
                  onClick={() => setMapOpen((v) => !v)}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                    padding: "8px 14px",
                    background: mapOpen ? "#1a73e8" : "#e8f0fe",
                    color: mapOpen ? "#fff" : "#1a73e8",
                    border: "1.5px solid #1a73e8",
                    borderRadius: 8,
                    cursor: "pointer",
                    fontWeight: 600,
                    fontSize: 13,
                    whiteSpace: "nowrap",
                    transition: "all 0.15s",
                  }}
                >
                  📍 {mapOpen ? "Close Map" : "Pick on Map"}
                </button>
              </div>
            </div>

            {/* ── Google Maps Panel ───────────────────────────────────────── */}
            {mapOpen && (
              <div style={{ marginTop: 10 }}>
                {!isLoaded ? (
                  <div
                    style={{
                      height: 300,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      background: "#f5f5f5",
                      borderRadius: 10,
                      color: "#888",
                      fontSize: 13,
                    }}
                  >
                    Loading Google Maps…
                  </div>
                ) : (
                  <>
                    {/* Google Places SearchBox */}
                    <StandaloneSearchBox
                      onLoad={onSearchBoxLoad}
                      onPlacesChanged={onPlacesChanged}
                    >
                      <input
                        type="text"
                        placeholder="Search a location (e.g. Coimbatore, Tamil Nadu)…"
                        style={{
                          width: "100%",
                          padding: "10px 14px",
                          marginBottom: 8,
                          border: "1.5px solid #d1d5db",
                          borderRadius: 8,
                          fontSize: 13,
                          boxSizing: "border-box",
                          outline: "none",
                          boxShadow: "0 2px 6px rgba(0,0,0,0.07)",
                        }}
                      />
                    </StandaloneSearchBox>

                    {/* Map */}
                    <GoogleMap
                      mapContainerStyle={MAP_CONTAINER}
                      center={mapCenter}
                      zoom={markerPos ? 14 : 5}
                      options={{
                        styles: MAP_STYLE,
                        disableDefaultUI: false,
                        zoomControl: true,
                        streetViewControl: false,
                        mapTypeControl: false,
                        fullscreenControl: false,
                      }}
                      onClick={handleMapClick}
                      onLoad={(map) => {
                        mapRef.current = map;
                      }}
                    >
                      {markerPos && (
                        <Marker
                          position={markerPos}
                          draggable
                          onDragEnd={handleMarkerDragEnd}
                          animation={window.google?.maps?.Animation?.DROP}
                        />
                      )}
                    </GoogleMap>

                    <p
                      style={{ margin: "6px 0 0", fontSize: 11, color: "#888" }}
                    >
                      Search a place, click on the map, or drag the pin —
                      address fields auto-fill.
                    </p>

                    {/* Live coords pill */}
                    {hasCoords && (
                      <div
                        style={{
                          marginTop: 8,
                          padding: "6px 12px",
                          background: "#e8f0fe",
                          borderRadius: 6,
                          fontSize: 12,
                          color: "#1a73e8",
                          fontWeight: 600,
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 6,
                        }}
                      >
                        📌{" "}
                        {parseFloat(form.location.coordinates.lat).toFixed(5)},{" "}
                        {parseFloat(form.location.coordinates.lng).toFixed(5)}
                      </div>
                    )}
                  </>
                )}
              </div>
            )}
          </section>
        </div>

        {/* Footer */}
        <div className="zone-modal-footer">
          <button className="btn-outline" onClick={onClose}>
            Cancel
          </button>
          <button
            className="btn btn-success"
            onClick={handleSubmit}
            disabled={!form.name.trim()}
          >
            {zone ? "Save changes" : "Create zone"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ZoneModal;
